import React, { useState, useEffect } from "react";
import { motion, useAnimation } from "framer-motion";
import { useInView } from "react-intersection-observer";
import emailjs from "emailjs-com";

const Contact = ({ data }) => {
  const [buttonMessage, setButtonMessage] = useState("Submit");
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const controls = useAnimation();
  const [ref, inView] = useInView({
    threshold: 0.2,
    triggerOnce: true,
  });

  useEffect(() => {
    if (inView) {
      controls.start("visible");
    }
  }, [controls, inView]);

  if (!data) return null;

  const {
    name,
    address: { street, city, state, zip },
    phone,
    contactmessage,
  } = data;

  const validateForm = (formData) => {
    const newErrors = {};
    const name = formData.get("from_name")?.trim();
    const email = formData.get("from_mail")?.trim();
    const message = formData.get("message")?.trim();

    // Name validation
    if (!name) {
      newErrors.name = "Name is required";
    }

    // Email validation
    if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      newErrors.email = "Please enter a valid email address";
    }

    // Message validation
    if (!message) {
      newErrors.message = "Message is required";
    }

    return newErrors;
  };

  const sendEmail = (e) => {
    e.preventDefault();
    setErrors({});

    // Validate form
    const formData = new FormData(e.target);
    const validationErrors = validateForm(formData);

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setIsSubmitting(true);
    setButtonMessage("Sending...");

    // NOTE: Replace these with your actual EmailJS credentials
    const SERVICE_ID = "service_nm266vr";
    const TEMPLATE_ID = "template_i8mcasp";
    const USER_ID = "mF10LHS3HrSbWSVRl";

    emailjs.sendForm(SERVICE_ID, TEMPLATE_ID, e.target, USER_ID).then(
      () => {
        setButtonMessage("Message Sent!");
        setIsSubmitting(false);
        e.target.reset();
        setTimeout(() => setButtonMessage("Submit"), 5000);
      },
      (error) => {
        console.log(error.text);
        setButtonMessage("Failed to Send");
        setIsSubmitting(false);
        setTimeout(() => setButtonMessage("Submit"), 3000);
      }
    );
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { duration: 0.8 } },
  };

  const formVariants = {
    hidden: { x: -30, opacity: 0 },
    visible: { x: 0, opacity: 1, transition: { duration: 0.7, ease: "easeInOut" } },
  };

  const infoVariants = {
    hidden: { x: 30, opacity: 0 },
    visible: { x: 0, opacity: 1, transition: { duration: 0.7, ease: "easeInOut", delay: 0.1 } },
  };

  return (
    <motion.section 
      id="contact" 
      ref={ref}
      variants={containerVariants}
      initial="hidden"
      animate={controls}
    >
      <div className="row section-head">
        <div className="two columns header-col">
          <h1>
            <span>Get In Touch.</span>
          </h1>
        </div>

        <div className="ten columns">
          <p className="lead">{contactmessage}</p>
        </div>
      </div>

      <div className="row">
        <motion.div className="eight columns" variants={formVariants}>
          <form className="contact-form" onSubmit={sendEmail}>
            <input type="hidden" name="contact_number" />
            
            <label>Name *</label>
            <input 
              type="text" 
              name="from_name"
              className={errors.name ? "error" : ""}
            />
            {errors.name && <span className="error-message">{errors.name}</span>}
            
            <label>Email</label>
            <input 
              type="email" 
              name="from_mail"
              className={errors.email ? "error" : ""}
            />
            {errors.email && <span className="error-message">{errors.email}</span>}
            
            <label>Message *</label>
            <textarea 
              name="message"
              className={errors.message ? "error" : ""}
            />
            {errors.message && <span className="error-message">{errors.message}</span>}
            
            <button
              className="submit"
              type="submit"
              disabled={isSubmitting}
            >
              {buttonMessage}
            </button>
          </form>
        </motion.div>

        <motion.aside className="four columns footer-widgets" variants={infoVariants}>
          <div className="widget widget_contact">
            <h4>Phone</h4>
            <p className="address">
              {name}
              <br />
              <span>{phone}</span>
            </p>
          </div>
        </motion.aside>
      </div>
    </motion.section>
  );
};

export default Contact;